import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fetchPrescriptionForModel } from '../../hooks/usePatientPrescriptionHistory';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  filterPatientPrescriptionHistory,
  prescriptionHistoryLabel,
  prescriptionHistoryMedicationLines,
  prescriptionHistorySubtitle,
} from '../../utils/prescriptionModelApply';

export interface PatientPrescriptionHistoryPickerProps {
  prescriptions: any[];
  loading?: boolean;
  onSelect: (prescription: any) => void;
  title?: string;
  searchPlaceholder?: string;
}

const PatientPrescriptionHistoryPicker: React.FC<PatientPrescriptionHistoryPickerProps> = ({
  prescriptions,
  loading = false,
  onSelect,
  title = 'Receituários anteriores deste paciente',
  searchPlaceholder = 'Pesquisar receituário anterior...',
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [popperWidth, setPopperWidth] = useState<number | undefined>();
  const [medLinesCache, setMedLinesCache] = useState<Record<string, string[]>>({});

  const medicationLinesFor = useCallback(
    (prescription: any) => {
      const id = String(prescription?.id || '');
      if (id && medLinesCache[id]?.length) return medLinesCache[id];
      return prescriptionHistoryMedicationLines(prescription);
    },
    [medLinesCache],
  );

  const prefetchMedicationLines = useCallback((prescription: any) => {
    const id = String(prescription?.id || '');
    if (!id || medLinesCache[id]?.length) return;
    if (prescriptionHistoryMedicationLines(prescription).length > 0) return;
    void fetchPrescriptionForModel(prescription).then((full) => {
      const lines = prescriptionHistoryMedicationLines(full);
      if (lines.length) {
        setMedLinesCache((prev) => ({ ...prev, [id]: lines }));
      }
    });
  }, [medLinesCache]);

  useLayoutEffect(() => {
    if (open && anchorRef.current) setPopperWidth(anchorRef.current.offsetWidth);
  }, [open]);

  const filtered = useMemo(
    () => filterPatientPrescriptionHistory(prescriptions, search),
    [prescriptions, search],
  );

  const handleSelect = (prescription: any) => {
    onSelect(prescription);
    setSearch('');
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ mb: 2.5, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <HistoryIcon sx={{ color: '#0d9488', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f766e' }}>
            {title}
          </Typography>
          {loading && <CircularProgress size={14} sx={{ color: '#0d9488' }} />}
        </Box>

        <Box ref={anchorRef}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            disabled={loading}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          sx={{ zIndex: 12, width: popperWidth }}
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <Paper
            elevation={4}
            sx={{
              width: popperWidth,
              maxHeight: 320,
              overflow: 'auto',
              borderRadius: 2,
              border: '1px solid #ccfbf1',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#94a3b8', px: 2, py: 1.5 }}>
                {search.trim()
                  ? `Nenhum receituário anterior encontrado para "${search.trim()}".`
                  : 'Nenhum receituário anterior para este paciente.'}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ display: 'block', px: 1.5, pt: 1.25, pb: 0.5, color: '#64748b' }}>
                  Clique para reutilizar medicamentos e dados clínicos (nova emissão com profissional atual)
                </Typography>
                <List dense disablePadding sx={{ py: 0.5 }}>
                  {filtered.map((prescription) => {
                    const medLines = medicationLinesFor(prescription);
                    const tooltipTitle = medLines.length
                      ? (
                        <Box component="span" sx={{ display: 'block', maxWidth: 380, py: 0.25 }}>
                          <Typography component="span" variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
                            {prescription?.format === 'free_text' ? 'Texto do receituário' : 'Medicamentos'}
                          </Typography>
                          {medLines.map((line, idx) => (
                            <Typography
                              key={`${prescription.id}-med-${idx}`}
                              component="span"
                              variant="caption"
                              sx={{ display: 'block', lineHeight: 1.45, whiteSpace: 'normal' }}
                            >
                              {prescription?.format === 'free_text' ? line : `${idx + 1}. ${line}`}
                            </Typography>
                          ))}
                        </Box>
                      )
                      : 'Nenhum medicamento neste receituário';

                    return (
                      <Tooltip
                        key={prescription.id}
                        title={tooltipTitle}
                        placement="right-start"
                        enterDelay={300}
                        enterNextDelay={200}
                        arrow
                      >
                        <ListItemButton
                          onMouseEnter={() => prefetchMedicationLines(prescription)}
                          onClick={() => handleSelect(prescription)}
                          sx={{ py: 0.85, '&:hover': { bgcolor: '#f0fdfa' } }}
                        >
                          <ListItemText
                            primary={prescriptionHistoryLabel(prescription)}
                            secondary={prescriptionHistorySubtitle(prescription) || undefined}
                            primaryTypographyProps={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}
                            secondaryTypographyProps={{ fontSize: '0.72rem', color: '#64748b' }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default PatientPrescriptionHistoryPicker;
